import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SearchOutlined, ExportOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import {
  Table, Input, Pagination, Tooltip, Select, Button, Tag, Modal, message,
} from 'antd';
import qs from 'qs';
import {
  countDomains, getCategoriesIndex, listDomains, archiveDomain, unarchiveDomain,
} from '../../services/Admin';
import { displayMetrics, getDomainStatusEmoji } from '../../utils/utils';

const ARCHIVE_ERROR_MESSAGES = {
  DOMAIN_NOT_FOUND: 'Domain not found.',
  DOMAIN_NOT_APPROVED: 'Only approved domains can be archived.',
  DOMAIN_ALREADY_ARCHIVED: 'Domain is already archived.',
  DOMAIN_NOT_ARCHIVED: "The domain wasn't archived.",
};

function Domains() {
  // Pagination
  const location = useLocation();
  const history = useHistory();
  const search = qs.parse(location.search, { ignoreQueryPrefix: true });
  const currentPage = +search.page || 1;
  const onPaginationChange = (page) => {
    const newSearch = {
      ...search,
      page,
    };
    history.push({
      ...location,
      search: qs.stringify(newSearch),
    });
  };
  // Search
  const onSearchChange = (e) => {
    const newSearch = {
      ...search,
      q: e.target.value,
      page: 1,
    };
    history.push({
      ...location,
      search: qs.stringify(newSearch),
    });
  };
  // Filter
  const domainFilter = search.f || 'ALL';
  const onFilterChange = (f) => {
    const newSearch = {
      ...search,
      f,
    };
    history.push({
      ...location,
      search: qs.stringify(newSearch),
    });
  };
  // Data retrieving
  const { isLoading, data: domains, refetch } = useQuery(
    ['listDomains', currentPage, search.q, domainFilter],
    () => listDomains({
      page: currentPage,
      q: search.q,
      f: domainFilter,
    }),
    { cacheTime: 0 },
  );
  // Archive / Unarchive
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const handleActionError = (error) => {
    const code = error.response?.data?.error;
    message.error(ARCHIVE_ERROR_MESSAGES[code] || 'Unexpected error while performing action!');
  };
  const onArchive = async () => {
    try {
      setIsPerformingAction(true);
      await archiveDomain(archiveTarget._id);
      message.success('Domain archived');
      setArchiveTarget(null);
      refetch();
    } catch (error) {
      handleActionError(error);
    }
    setIsPerformingAction(false);
  };
  const onUnarchive = async (domain) => {
    try {
      setIsPerformingAction(true);
      await unarchiveDomain(domain._id);
      message.success('Domain unarchived');
      refetch();
    } catch (error) {
      handleActionError(error);
    }
    setIsPerformingAction(false);
  };
  const { data: count, isLoading: isCountLoading } = useQuery(
    ['countDomains', search.q, domainFilter],
    () => countDomains({ q: search.q, f: domainFilter }),
    { cacheTime: 0 },
  );
  const { data: categoriesIndex } = useQuery(
    ['getCategoriesIndex'],
    () => getCategoriesIndex(),
  );
  return (
    <div id="Users">
      <div className="Filters">
        <Select
          value={domainFilter}
          style={{ width: 150, marginRight: 8 }}
          onChange={onFilterChange}
        >
          <Select.Option value="ALL">All</Select.Option>
          <Select.Option value="LIVE">Live</Select.Option>
          <Select.Option value="PAUSED">Paused</Select.Option>
          <Select.Option value="REJECTED">Rejected</Select.Option>
          <Select.Option value="TO_REVIEW">To Review / Other</Select.Option>
          <Select.Option value="ARCHIVED">Archived</Select.Option>
        </Select>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search in domain name"
          defaultValue={search.q || ''}
          style={{ width: 260 }}
          onChange={debounce(onSearchChange, 500)}
          allowClear
        />
        <div style={{ flex: 1 }} />
        <Pagination
          current={currentPage}
          onChange={onPaginationChange}
          total={count}
          pageSize={20}
          showSizeChanger={false}
        />
      </div>
      <Table
        dataSource={domains}
        loading={isLoading}
        pagination={false}
        rowKey="_id"
        size="small"
        // scroll={{ x: 1500 }}
      >
        <Table.Column
          title=""
          key="_id"
          render={(_, d) => (
            <Tooltip
              placement="right"
              title={(
                // eslint-disable-next-line no-nested-ternary
                d.status === 'APPROVED' && !d.live
                  ? [...d.logs].reverse().find((l) => l.type === 'PAUSE' && l.params.manual)?.params.reason
                  : (
                    d.status === 'REJECTED'
                      ? [...d.logs].reverse().find((l) => l.type === 'REJECT')?.params.reason
                      : ''
                  )
              )}
            >
              {getDomainStatusEmoji(d)}
            </Tooltip>
          )}
        />
        <Table.Column
          title={`Name (${isCountLoading ? '...' : count})`}
          key="_id"
          render={(_, d) => (
            <div>
              {d.name}
              {' '}
              <a href={`https://${d.name}`} target="_blank" rel="noreferrer">
                <ExportOutlined />
              </a>
            </div>
          )}
        />
        <Table.Column
          title="☯️"
          key="_id"
          render={(_, d) => d.points || '-'}
        />
        <Table.Column
          title="Metrics"
          key="_id"
          render={(_, d) => (d.metrics ? displayMetrics(d) : '')}
        />
        <Table.Column
          title="Description"
          key="_id"
          dataIndex="description"
        />
        <Table.Column
          title="Categories"
          key="_id"
          render={(_, d) => (
            <Tooltip
              title={d.categories.length > 1 ? (
                <div>
                  {d.categories.map((category) => (
                    <div key={category}>
                      {categoriesIndex?.[category].name}
                    </div>
                  ))}
                </div>
              ) : null}
            >
              {categoriesIndex?.[d.categories[0]].name}
              {d.categories.length > 1 ? `, +${d.categories.length - 1} more` : ''}
            </Tooltip>
          )}
        />
        <Table.Column
          title="Users"
          key="_id"
          render={(_, d) => (
            <div>
              {d.userIds.map((user) => (
                <div key={user._id}>
                  <a href={`#/users/?q=${user.email}`}>
                    {user.email}
                  </a>
                </div>
              ))}
            </div>
          )}
        />
        <Table.Column
          title="Actions"
          key="_id"
          render={(_, d) => {
            if (d.archivedAt) {
              return (
                <div>
                  <Tag color="default">Archived</Tag>
                  <Button
                    size="small"
                    disabled={isPerformingAction}
                    onClick={() => onUnarchive(d)}
                  >
                    Unarchive
                  </Button>
                </div>
              );
            }
            if (d.status === 'APPROVED') {
              return (
                <Button
                  size="small"
                  danger
                  disabled={isPerformingAction}
                  onClick={() => setArchiveTarget(d)}
                >
                  Archive
                </Button>
              );
            }
            return null;
          }}
        />
      </Table>
      {archiveTarget && (
        <Modal
          visible
          title="Archive Domain"
          footer={null}
          closable={false}
        >
          <p>
            {`Archive ${archiveTarget.name}? It will be hidden from everyone and stop appearing as an opportunity. You can unarchive it later.`}
          </p>
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button
              disabled={isPerformingAction}
              onClick={() => setArchiveTarget(null)}
            >
              CANCEL
            </Button>
            {' '}
            <Button
              danger
              type="primary"
              loading={isPerformingAction}
              onClick={onArchive}
            >
              ARCHIVE
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

Domains.propTypes = {
};

export default Domains;
